import React, { createContext, useContext, useMemo } from "react";

export type DateGlobalConfig = {
  locale: string;
  use12Hour: boolean;
  showSeconds: boolean;
  useOrdinal: boolean;
  timezone: string;
};

const defaultConfig: DateGlobalConfig = {
  locale: navigator.language || "en-US",
  use12Hour: true,
  showSeconds: true,
  useOrdinal: true,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

const DateConfigContext = createContext<DateGlobalConfig>(defaultConfig);

export const DateProvider = ({
  children,
  value,
}: {
  children: React.ReactNode;
  value?: Partial<DateGlobalConfig>;
}) => {
  // Rebuilt only when a setting changes: a new object every render made
  // every useFormatDate caller (and the memos depending on it) recompute.
  const config = useMemo(
    () => ({ ...defaultConfig, ...value }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the settings, not the object identity
    [
      value?.locale,
      value?.use12Hour,
      value?.showSeconds,
      value?.useOrdinal,
      value?.timezone,
    ],
  );
  return (
    <DateConfigContext.Provider value={config}>
      {children}
    </DateConfigContext.Provider>
  );
};

export const useDateConfig = () => useContext(DateConfigContext);
